import { ModelError_Item, type ModelError_Response } from '@treadinc/horizon-api-spec';
import axios, { type AxiosError, isAxiosError } from 'axios';

// Shared helper for pulling a human-readable message + HTTP
// status off an Axios error. Mirrors the 422/400/404 parsing path inside
// `connectionModule.handleErrorResponse` (which reads `error.errors[].message`
// out of the standard Tread API error envelope, see `ModelError_Item`).
// Surfaced here so non-toast call sites (e.g. inline form errors) can
// reuse the same extraction without going through the global toast/Sentry
// pipeline.
//
// The UI typically wants two things from a failed request:
//   1. A user-actionable message when the backend returned validation
//      detail (almost always 422 in our API).
//   2. The HTTP status, so it can decide whether the message is worth
//      showing inline at all — 5xx / network messages aren't actionable
//      and the UI should fall back to a "please contact support" copy.
interface AxiosErrorInfo {
  message: string;
  status?: number;
}

// The one upload-failure class worth rendering inline next to an upload
// control: a 422 means the backend rejected the file itself (mismatched
// content type, corrupt file, etc.) and the response body carries a
// user-actionable reason (mirrors the Tread Agent composer's
// attachment-rejection handling). Every other status stays on the generic
// toast/Sentry pipeline. Upload hooks use this to decide whether to re-throw
// untoasted, and `useInlineUploadError` uses it to decide whether to render
// the message — keep both layers on this single predicate so the policy can't
// drift.
export const isUploadValidationError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 422;

// A DELETE that 404s means the record is already gone, so the request's desired
// end state is already true. Delete call sites use this to run their success
// path instead of surfacing an error — keep them on this single predicate so
// the policy can't drift.
export const isNotFoundError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 404;

export const isConflictError = (error: unknown): boolean =>
  isAxiosError(error) && error.response?.status === 409;

export const extractAxiosErrorInfo = (err: unknown, fallback: string): AxiosErrorInfo => {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const data = err.response?.data as
      | { error?: { errors?: Array<Partial<ModelError_Item>> } }
      | undefined;
    const items = data?.error?.errors ?? [];
    const joined = items
      .map((item) => item.message)
      .filter((m): m is string => Boolean(m))
      .join('; ');
    if (joined) return { message: joined, status };
    if (status) return { message: `${status}: ${err.message ?? fallback}`, status };
    return { message: err.message ?? fallback };
  }
  if (err instanceof Error && err.message) return { message: err.message };
  return { message: fallback };
};

// The per-field messages in a validation error, for forms that show each one under its
// field. Missing on a malformed or non-envelope response, hence the fallbacks.
export const extractFieldErrors = (error: AxiosError<ModelError_Response>) =>
  error.response?.data?.error?.errors ?? [];
